import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ImpersonationBanner from './components/common/ImpersonationBanner';
import { BusinessTypeProvider } from './context/BusinessTypeContext';
import { ErrorProvider, ErrorDisplay } from './context/ErrorContext';
import { ColorModeProvider } from './context/ColorModeContext';
import ErrorBoundary from './components/ErrorBoundary'; 
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import Login from './features/auth/pages/Login';
// Revisión de los bloqueos PRO (solo desarrollo; Vite elimina la rama en el build de producción).
const ProLockedPreview = import.meta.env.DEV ? React.lazy(() => import('./components/pro/ProLockedPreview')) : null;
import Register from './features/auth/pages/Register';
import RestablecerPassword from './features/auth/pages/RestablecerPassword';
import OnboardingWizardPage from './features/onboarding/pages/OnboardingWizardPage';
import UserSettingsPage from './features/auth/pages/UserSettingsPage';
// Import route configurations
import { DashboardRoutes } from './features/dashboard/routes';
import { BusinessRoutes } from './features/business/routes';
import { ClientRoutes } from './features/clients/routes';
import { AdminRoutes } from './features/admin/routes';
import { RewardsRoutes } from './features/rewards/routes';
import { MembershipsRoutes } from './features/memberships/routes';   // Membresías F1 (20-oct-2026)
import { SubscriptionRoutes } from './features/subscription/routes';
import { AnalyticsRoutes } from './features/analytics/routes';
import { CampaignsRoutes } from './features/campaigns/routes';
import { TasksRoutes } from './features/tasks/routes';
import { DeliveryRoutes, CourierRoutes } from './features/delivery/routes';
import { WhatsAppRoutes } from './features/whatsapp/routes';
import { GiftCardRoutes } from './features/gift-cards/routes';
import { FAQRoutes } from './features/faq/routes';
import { LegalRoutes } from './features/legal/routes';
import { TeamRoutes } from './features/team/routes';
import { ClientRequestsRoutes } from './features/requests/routes';

// Declarar gtag para TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// Reporta cada cambio de ruta a Google Analytics (SPA)
const RouteTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-R8BV4P5BDS', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  return null;
};

function App() {
  // La auto-actualización (la pestaña se recarga sola al detectar un deploy nuevo)
  // vive en src/autoUpdate.ts, inicializada en main.tsx. Ya no hay botón manual.

  return (
    <ColorModeProvider>
    <ErrorProvider>
      <AuthProvider>
        <Router>
          <RouteTracker />
          <Toaster position="top-center" reverseOrder={false} />
          <ErrorDisplay />
          <ImpersonationBanner />
          
          <ErrorBoundary>
            <BusinessTypeProvider>
              <Routes>
                {/* Restablecer contraseña: destino del enlace del correo de recuperación de base
                    (/restablecer?token=...). Público y SIN guards: el usuario llega desde el correo.
                    Reemplaza al viejo handler de Firebase /auth/action. */}
                <Route path="/restablecer" element={<RestablecerPassword />} />

                {/* Activar cuenta: destino del enlace de INVITACIÓN de operador
                    (/activar?token=...). Mismo componente que /restablecer en modo invite.
                    Público y SIN guards: el invitado llega desde el correo sin sesión. */}
                <Route path="/activar" element={<RestablecerPassword mode="invite" />} />

                {/* Repartidor: enlace personal /repartidor/<token>, público y SIN guards (módulo delivery). */}
                {CourierRoutes}

                {ProLockedPreview && (
                  <Route path="/dev/pro-locked" element={<React.Suspense fallback={null}><ProLockedPreview /></React.Suspense>} />
                )}

                {/* Public routes */}
                <Route element={<PublicRoute />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                </Route>
                
                {/* Private routes */}
                <Route element={<PrivateRoute />}>
                  {/* Wizard de onboarding (pantalla completa, sin Layout) */}
                  <Route path="/onboarding" element={<OnboardingWizardPage />} />
                  {DashboardRoutes}
                  {BusinessRoutes}
                  {ClientRoutes}
                  {AdminRoutes}
                  {RewardsRoutes}
                  {MembershipsRoutes}
                  {SubscriptionRoutes}
                  {AnalyticsRoutes}
                  {CampaignsRoutes}
                  {TasksRoutes}
                  {DeliveryRoutes}
                  {WhatsAppRoutes}
                  {GiftCardRoutes}
                  {FAQRoutes}
                  {LegalRoutes}
                  {TeamRoutes}
                  {ClientRequestsRoutes}
                  <Route path="/dashboard/settings" element={<UserSettingsPage />} />
                </Route>
                
                {/* Default redirects */}
                <Route path="/" element={<Navigate to="/login" />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
            </BusinessTypeProvider>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </ErrorProvider>
    </ColorModeProvider>
  );
}

export default App;